/**
 * Brand design tokens.
 *
 * The site is structured around two brand roles that every component and the
 * MUI palette already rely on:
 *   - `accent`  -> MUI `primary.main`   (the CTA / highlight colour; green in RSMC)
 *   - `surface` -> MUI `secondary.main` (the deep brand header/footer surface)
 *
 * Each brand supplies a `light` and a `dark` token set with the same shape.
 * Which one ships is fixed by {@link DEFAULT_MODE} — there is no runtime
 * switcher. `deriveTokens()` fills in a few sensible defaults so brand
 * definitions stay compact.
 *
 * These tokens are the single source of truth: `createAppTheme` builds the MUI
 * theme from them and `applyCssVars` mirrors them onto CSS custom properties so
 * plain CSS files (and any `var(--color-*)` usage) stay in lockstep.
 */

const DESTRUCTIVE = '#ec1313';
const DESTRUCTIVE_TEXT = '#ffffff';

/**
 * @typedef {Object} BrandTokens
 * @property {string} accent
 * @property {string} accentLight
 * @property {string} accentDark
 * @property {string} accentText      Text/icon colour on top of `accent`.
 * @property {string} surface         Dark brand surface (header/footer/drawer).
 * @property {string} surfaceLight
 * @property {string} surfaceDark
 * @property {string} surfaceText     Text/icon colour on top of `surface`.
 * @property {string} heading         Heading colour on light backgrounds.
 * @property {string} background      Page background.
 * @property {string} backgroundLight Subtle alternating section background.
 * @property {string} card            Card / paper background.
 * @property {string} cardText        Text on cards.
 * @property {string} text            Body text.
 * @property {string} textMuted       Secondary / muted text.
 * @property {string} textLight       Tertiary text.
 * @property {string} muted           Muted fill (chips, subtle sections).
 * @property {string} mutedText
 * @property {string} border
 * @property {string} input
 * @property {string} focusRing
 * @property {string} destructive
 * @property {string} destructiveText
 * @property {string[]} charts        Five data-visualisation colours.
 */

/**
 * Fill in defaults so brand mode definitions can stay terse.
 * @param {Partial<BrandTokens>} t
 * @returns {BrandTokens}
 */
const deriveTokens = (t) => ({
  accentText: '#ffffff',
  surfaceLight: t.surface,
  surfaceDark: t.surface,
  surfaceText: '#ffffff',
  heading: t.text,
  backgroundLight: t.background,
  card: t.background,
  cardText: t.text,
  textLight: t.textMuted,
  muted: t.backgroundLight || t.background,
  mutedText: t.textMuted,
  input: t.border,
  focusRing: t.accent,
  destructive: DESTRUCTIVE,
  destructiveText: DESTRUCTIVE_TEXT,
  ...t,
});

/**
 * Each brand: metadata + its light/dark token sets.
 * `swatch` is the brand's representative colour.
 */
export const BRANDS = {
  rsmc: {
    key: 'rsmc',
    label: 'RSMC',
    description: 'Ready Secure Managed Cloud — green & charcoal',
    swatch: '#779A0A',
    light: deriveTokens({
      accent: '#779A0A',
      accentLight: '#b5da5f',
      accentDark: '#597307',
      accentText: '#1A1A1A',
      surface: '#1A1A1A',
      surfaceLight: '#222222',
      surfaceDark: '#0f0f0f',
      heading: '#1A1A1A',
      background: '#ffffff',
      backgroundLight: '#f8f9fa',
      text: '#1A1A1A',
      textMuted: '#6c757d',
      textLight: '#4a4a4a',
      muted: '#e9ecef',
      border: '#e0e0e0',
      input: '#f1f3f5',
      charts: ['#779A0A', '#b5da5f', '#597307', '#1A1A1A', '#8fb51a'],
    }),
    dark: deriveTokens({
      accent: '#8fb51a',
      accentLight: '#b5da5f',
      accentDark: '#779A0A',
      accentText: '#10130a',
      surface: '#0f0f0f',
      surfaceLight: '#1e1e1e',
      surfaceDark: '#050505',
      heading: '#f5f5f5',
      background: '#121212',
      backgroundLight: '#1a1a1a',
      card: '#1e1e1e',
      text: '#f5f5f5',
      textMuted: '#a0a0a0',
      muted: '#2a2a2a',
      border: 'rgba(255, 255, 255, 0.12)',
      input: '#242424',
      charts: ['#8fb51a', '#b5da5f', '#597307', '#e0e0e0', '#779A0A'],
    }),
  },

  ellafin: {
    key: 'ellafin',
    label: 'EllaFin',
    description: 'Navy & lime — finance-first FinOps',
    // Matches the live EllaFin site (ellafin.ai): dark navy chrome, lime-green
    // accent, warm cream light background.
    swatch: '#1e2140',
    light: deriveTokens({
      accent: '#7e9419',
      accentLight: '#96b024',
      accentDark: '#657814',
      accentText: '#ffffff',
      surface: '#1e2140',
      surfaceLight: '#2a2e52',
      surfaceDark: '#13152b',
      heading: '#1e2140',
      background: '#f4f0eb',
      backgroundLight: '#ece7df',
      card: '#ffffff',
      text: '#0f1729',
      textMuted: '#4d6280',
      muted: '#e7eeee',
      border: '#d7dfea',
      input: '#e1e7ef',
      focusRing: '#7e9419',
      charts: ['#7e9419', '#96b024', '#5e4d89', '#9b7cb6', '#90acad'],
    }),
    dark: deriveTokens({
      accent: '#96b024',
      accentLight: '#aecb3a',
      accentDark: '#7e9419',
      accentText: '#12160a',
      surface: '#13152b',
      surfaceLight: '#1e2140',
      surfaceDark: '#0c0e1c',
      heading: '#e8ebf5',
      background: '#13152b',
      backgroundLight: '#1a1d38',
      card: '#1e2140',
      text: '#eef1f8',
      textMuted: '#9aa6be',
      muted: '#242847',
      border: 'rgba(255, 255, 255, 0.12)',
      input: '#202444',
      focusRing: '#96b024',
      charts: ['#96b024', '#aecb3a', '#9b7cb6', '#90acad', '#7e9419'],
    }),
  },

  ocean: {
    key: 'ocean',
    label: 'Ocean',
    description: 'Teal & slate — calm and technical',
    swatch: '#0d9488',
    light: deriveTokens({
      accent: '#0d9488',
      accentLight: '#14b8a6',
      accentDark: '#0f766e',
      accentText: '#ffffff',
      surface: '#0f172a',
      surfaceLight: '#1e293b',
      surfaceDark: '#0a0f1e',
      heading: '#0f766e',
      background: '#ffffff',
      backgroundLight: '#f0f9f8',
      text: '#0f172a',
      textMuted: '#475569',
      muted: '#e2e8f0',
      border: '#cbd5e1',
      input: '#eef2f6',
      charts: ['#0d9488', '#14b8a6', '#0ea5e9', '#6366f1', '#0f766e'],
    }),
    dark: deriveTokens({
      accent: '#2dd4bf',
      accentLight: '#5eead4',
      accentDark: '#14b8a6',
      accentText: '#04201c',
      surface: '#0b1120',
      surfaceLight: '#1e293b',
      surfaceDark: '#060a14',
      heading: '#7dd3cc',
      background: '#0b1120',
      backgroundLight: '#111a2e',
      card: '#131c2e',
      text: '#e2e8f0',
      textMuted: '#94a3b8',
      muted: '#1e293b',
      border: 'rgba(255, 255, 255, 0.12)',
      input: '#18233a',
      focusRing: '#2dd4bf',
      charts: ['#2dd4bf', '#5eead4', '#38bdf8', '#818cf8', '#14b8a6'],
    }),
  },

  sunset: {
    key: 'sunset',
    label: 'Sunset',
    description: 'Amber & warm stone — bold and inviting',
    swatch: '#ea580c',
    light: deriveTokens({
      accent: '#ea580c',
      accentLight: '#fb923c',
      accentDark: '#c2410c',
      accentText: '#ffffff',
      surface: '#1c1917',
      surfaceLight: '#292524',
      surfaceDark: '#0f0d0c',
      heading: '#9a3412',
      background: '#ffffff',
      backgroundLight: '#fdf6f0',
      text: '#1c1917',
      textMuted: '#78716c',
      muted: '#f0e9e4',
      border: '#e7ddd5',
      input: '#f5efe9',
      charts: ['#ea580c', '#f59e0b', '#ef4444', '#d97706', '#9a3412'],
    }),
    dark: deriveTokens({
      accent: '#fb923c',
      accentLight: '#fdba74',
      accentDark: '#ea580c',
      accentText: '#1c0f05',
      surface: '#1a1512',
      surfaceLight: '#292524',
      surfaceDark: '#0f0b08',
      heading: '#fdba74',
      background: '#1a1512',
      backgroundLight: '#221a14',
      card: '#26201c',
      text: '#f5efe9',
      textMuted: '#b8a99c',
      muted: '#2a231e',
      border: 'rgba(255, 255, 255, 0.12)',
      input: '#2a221c',
      focusRing: '#fb923c',
      charts: ['#fb923c', '#fdba74', '#f87171', '#fbbf24', '#f59e0b'],
    }),
  },
};

/** Ordered list of brand keys for building menus. */
export const BRAND_KEYS = Object.keys(BRANDS);

/**
 * The brand the site ships with. EllaFin is the chosen palette; the other
 * entries above are kept as data so a palette can be swapped back in by
 * changing this one line, but they are no longer reachable from the UI.
 */
export const DEFAULT_BRAND = 'ellafin';

/**
 * The colour mode the site ships. There is no runtime switch — this constant
 * picks which of the active brand's two token sets is used everywhere, so the
 * site can be flipped between the light and dark palette on one line.
 */
export const DEFAULT_MODE = 'dark';

/**
 * Resolve the flat token set for a brand, falling back safely.
 * @param {string} brandKey
 * @returns {BrandTokens}
 */
export const getTokens = (brandKey) => {
  const brand = BRANDS[brandKey] || BRANDS[DEFAULT_BRAND];
  return brand[DEFAULT_MODE] || brand.light;
};
